import { useTheme } from "@mui/material/styles";
import { Box, Typography, Divider } from "@mui/material";
import { tokens } from "../theme";

const COLOR_GROUPS = [
  { key: "primary",      label: "Primary",       steps: [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { key: "primarydark",  label: "Primary Dark",  steps: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { key: "secondary",    label: "Secondary",     steps: [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { key: "secondarydark",label: "Secondary Dark",steps: [100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { key: "tertiary",     label: "Tertiary",      steps: [50, 80, 100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { key: "quartiary",    label: "Quartiary",     steps: [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { key: "grey",         label: "Grey",          steps: [10, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900] },
  { key: "greydark",     label: "Grey Dark",     steps: [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 930, 940, 950, 955, 960, 965, 970, 975, 980] },
  { key: "green",        label: "Green",         steps: [200, 300, 400, 500] },
  { key: "orange",       label: "Orange",        steps: [300, 400, 500] },
  { key: "red",          label: "Red",           steps: [200, 300, 400, 500] },
];

const TYPOGRAPHY_VARIANTS = [
  { variant: "h1",        label: "h1 — Heading 1" },
  { variant: "h2",        label: "h2 — Heading 2" },
  { variant: "h3",        label: "h3 — Heading 3" },
  { variant: "h4",        label: "h4 — Heading 4" },
  { variant: "h5",        label: "h5 — Heading 5" },
  { variant: "h6",        label: "h6 — Heading 6" },
  { variant: "number0",   label: "number0 — Poppins XL" },
  { variant: "number1",   label: "number1 — Poppins LG" },
  { variant: "number",    label: "number — Poppins MD" },
  { variant: "number2",   label: "number2 — Helvetia" },
  { variant: "number3",   label: "number3 — Poppins SM" },
  { variant: "number4",   label: "number4 — Poppins Base" },
  { variant: "subtitle1", label: "subtitle1" },
  { variant: "subtitle2", label: "subtitle2" },
  { variant: "body1",     label: "body1" },
  { variant: "body2",     label: "body2" },
  { variant: "button",    label: "button" },
];

function isLight(hex) {
  const c = hex.replace(/\s/g, "");
  if (c.startsWith("hsl")) {
    const m = c.match(/hsl\(\s*[\d.]+,\s*[\d.]+%,\s*([\d.]+)%/);
    if (m) return parseFloat(m[1]) > 60;
  }
  return true;
}

function ColorSwatch({ color, label }) {
  const textColor = isLight(color) ? "#1a1a1a" : "#f5f5f5";
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, minWidth: 64 }}>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: 1.5,
          backgroundColor: color,
          border: "1px solid rgba(128,128,128,0.15)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      />
      <Typography variant="body2" sx={{ fontSize: "0.6rem", color: "text.secondary", textAlign: "center", lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontSize: "0.55rem", color: "text.disabled", textAlign: "center", lineHeight: 1.2, fontFamily: "monospace" }}>
        {color}
      </Typography>
    </Box>
  );
}

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: "0.75rem", color: "text.disabled" }}>
        {title}
      </Typography>
      <Divider sx={{ mb: 3 }} />
      {children}
    </Box>
  );
}

export default function DesignSystemPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 6 }, py: 6 }}>

      <Box sx={{ mb: 8 }}>
        <Typography variant="h1" sx={{ mb: 1 }}>Design System</Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Color palette · Typography · Breakpoints · Spacing — mode: <strong>{theme.palette.mode}</strong>
        </Typography>
      </Box>

      {/* ── COLORS ── */}
      <Section title="Color Palettes">
        {COLOR_GROUPS.map(({ key, label, steps }) => {
          const group = colors[key];
          if (!group) return null;
          return (
            <Box key={key} sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>{label}</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                {steps.map((step) => {
                  const val = group[step];
                  if (!val) return null;
                  return <ColorSwatch key={step} color={val} label={`${step}`} />;
                })}
              </Box>
            </Box>
          );
        })}

        {/* Traffic lights */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>Traffic (health indicators)</Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            {["green", "orange", "red"].map((k) => (
              <ColorSwatch key={k} color={colors[k]?.[500] ?? ""} label={k} />
            ))}
          </Box>
        </Box>
      </Section>

      {/* ── TYPOGRAPHY ── */}
      <Section title="Typography">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {TYPOGRAPHY_VARIANTS.map(({ variant, label }) => (
            <Box key={variant} sx={{ display: "flex", alignItems: "baseline", gap: 3, borderBottom: "1px solid", borderColor: "divider", pb: 2 }}>
              <Box sx={{ minWidth: 160, flexShrink: 0 }}>
                <Typography variant="body2" sx={{ fontSize: "0.65rem", color: "text.disabled", fontFamily: "monospace" }}>{label}</Typography>
              </Box>
              <Typography variant={variant}>
                The quick brown fox jumps over the lazy dog
              </Typography>
            </Box>
          ))}
        </Box>
      </Section>

      {/* ── BREAKPOINTS ── */}
      <Section title="Breakpoints">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {Object.entries(theme.breakpoints.values).map(([key, value]) => (
            <Box key={key} sx={{ px: 3, py: 2, border: "1px solid", borderColor: "divider", borderRadius: 2, minWidth: 120, textAlign: "center" }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{key}</Typography>
              <Typography variant="subtitle1" color="text.secondary">{value}px</Typography>
            </Box>
          ))}
        </Box>
      </Section>

      {/* ── SPACING ── */}
      <Section title="Spacing (base unit = 8px)">
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2 }}>
          {[0.5, 1, 2, 3, 4, 5, 6, 8, 10, 12].map((n) => (
            <Box key={n} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: theme.spacing(n), height: theme.spacing(n), backgroundColor: "primary.main", borderRadius: 0.5, minWidth: 4 }} />
              <Typography variant="body2" sx={{ fontSize: "0.6rem", color: "text.disabled" }}>
                {n} · {theme.spacing(n)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Section>

      {/* ── PALETTE ROLES ── */}
      <Section title="Semantic Palette Roles">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          {[
            { label: "background.default", color: theme.palette.background.default },
            { label: "background.paper",   color: theme.palette.background.paper },
            { label: "primary.main",       color: theme.palette.primary.main },
            { label: "secondary.main",     color: theme.palette.secondary.main },
            { label: "tertiary.main",      color: theme.palette.tertiary?.main },
            { label: "quartiary.main",     color: theme.palette.quartiary?.main },
            { label: "traffic.green",      color: theme.palette.traffic?.green },
            { label: "traffic.orange",     color: theme.palette.traffic?.orange },
            { label: "traffic.red",        color: theme.palette.traffic?.red },
          ].filter(({ color }) => color).map(({ label, color }) => (
            <ColorSwatch key={label} color={color} label={label} />
          ))}
        </Box>
      </Section>

    </Box>
  );
}
